import { Injectable } from '@angular/core';
import { Observer, Observable } from 'rxjs';
import { GOOGLEMAPS_KEY, LANGUAGE } from '../constants/global';
import { ErrorTreatmentFunctions } from '../modules/treatments.module';

// Replace this by anything without an ID_KEY
const getScriptSrc = (callbackName, googlemapsKey, language) => {
  // return `https://maps.googleapis.com/maps/api/js?key=YOUR_API_KEY&callback=${callbackName}`;
  return `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${googlemapsKey}&callback=${callbackName}&language=${language}&loading=async`;
};

/**
 * GeocodingService class.
 * https://developers.google.com/maps/documentation/javascript/
 */
@Injectable() export class GeocodingService {
  private map: google.maps.Map;
  private marker: google.maps.Marker;
  private geocoder: google.maps.Geocoder;
  private routeMatrixPromise: Promise<any>;
  private scriptLoadingPromise: Promise<void>;
  private _errorTreat: ErrorTreatmentFunctions;

  constructor() {
    // Loading script
    this.loadScriptLoadingPromise();
    // Loading other components
    this.onReady().then(() => {
      try {
        this.geocoder = new google.maps.Geocoder()
      } catch (error) {
        console.log(error)
      }

    });
  }

  /**
   * Lazily loads the "routes" library (RouteMatrix), caching the promise so
   * it's only imported once regardless of how many calculateDistance() calls happen.
   */
  private getRouteMatrix(): Promise<any> {
    if (!this.routeMatrixPromise) {
      this.routeMatrixPromise = this.onReady()
        .then(() => (<any>google.maps).importLibrary('routes'))
        .then((routesLibrary: any) => routesLibrary.RouteMatrix);
    }
    return this.routeMatrixPromise;
  }

  onReady(): Promise<void> {
    return this.scriptLoadingPromise;
  }

  /**
   * Reverse geocoding by location.
   *
   * Wraps the Google Maps API geocoding service into an observable.
   *
   * @param latLng Location
   * @return An observable of GeocoderResult
   */
  geocode(latLng: google.maps.LatLng): Observable<google.maps.GeocoderResult[]> {
    return new Observable((observer: Observer<google.maps.GeocoderResult[]>) => {
      this.onReady().then(() => {
        if (this.geocoder) {
          // Invokes geocode method of Google Maps API geocoding.
          this.geocoder.geocode({ 'location': latLng }, (
            (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
              if (status === google.maps.GeocoderStatus.OK) {
                observer.next(results);
                observer.complete();
              }
            })
          );
        }
      });
    });
  }

  /**
   * Geocoding services.
   *
   * Wraps the Google Maps API geocoding service into an observable.
   *
   * @param address The address to be searched
   * @return An observable of GeocoderResult
   */
  codeAddress(address: string): Observable<google.maps.GeocoderResult[]> {
    
    return new Observable((observer: Observer<google.maps.GeocoderResult[]>) => {

      this.onReady().then(() => {
        if (this.geocoder) {
          // Invokes geocode method of Google Maps API geocoding.
          this.geocoder.geocode({ 'address': address }, (
            (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
             
              if (status === google.maps.GeocoderStatus.OK) {
                observer.next(results);
                observer.complete();
              }

            })
          );
        }
      });
    });

  }

  /**
   * Route Matrix API (replacement for the deprecated DistanceMatrixService).
   *
   * Obter a distância entre dois pontos.
   *
   * @param origin The origin address
   * @param destination The destination address
   * @return An observable shaped like the legacy DistanceMatrixResponse
   *   (rows[0].elements[0].distance/.duration), so existing callers don't need to change.
   */
  calculateDistance(origin: any, destination: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.getRouteMatrix().then(async (RouteMatrix: any) => {
        try {
          const response = await RouteMatrix.computeRouteMatrix({
            origins: [origin],
            destinations: [destination],
            travelMode: google.maps.TravelMode.DRIVING,
            units: google.maps.UnitSystem.METRIC,
            fields: ['distanceMeters', 'durationMillis', 'condition'],
          });

          const item = response?.matrix?.rows?.[0]?.items?.[0];

          if (item && item.condition === 'ROUTE_EXISTS') {
            observer.next({
              rows: [{
                elements: [{
                  status: 'OK',
                  distance: { value: item.distanceMeters },
                  duration: { value: Math.round(item.durationMillis / 1000) },
                }]
              }]
            });
            observer.complete();
          }
        } catch (error) {
          console.log(error);
        }
      });
    });
  }

  async initMap(mapHtmlElement: HTMLElement, options: google.maps.MapOptions): Promise<google.maps.Map> {
    await this.onReady();

    this.map = new google.maps.Map(mapHtmlElement, options);

    let marker = new google.maps.Marker({
      position: options.center,
      map: this.map
    });

    return this.map;
  }

  private loadScriptLoadingPromise() {
    const script = window.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    const callbackName: string = 'UNIQUE_NAME_HERE';
    script.src = getScriptSrc(callbackName, GOOGLEMAPS_KEY, LANGUAGE);
    try {
      
    
    this.scriptLoadingPromise = new Promise<void>((resolve: Function, reject: Function) => {
      (<any>window)[callbackName] = () => { resolve(); };

      script.onerror = (error: Event) => { reject(error); };
    });
  } catch (error) {
    this._errorTreat.treatErrorResponse(error);
  }
    window.document.body.appendChild(script);
  }
}
